import { useEffect, useId } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { MarketSpotlight } from "@/hooks/useMarketSpotlights";
import { useUserProfile } from "@/hooks/useUserProfile";

// ── localStorage helpers (unauthenticated fallback) ──────────────────────────

function isLocallyDismissed(id: string): boolean {
  try { return localStorage.getItem(`dismissed_spotlight_${id}`) === "1"; } catch { return false; }
}

function localDismiss(id: string) {
  try { localStorage.setItem(`dismissed_spotlight_${id}`, "1"); } catch {}
}

// ── All unseen approved spotlights ───────────────────────────────────────────

/**
 * Returns all approved, non-expired spotlights the current user hasn't seen.
 * - Authenticated users: filters against `user_spotlight_reads` in DB.
 * - Unauthenticated users: filters against `dismissed_spotlight_*` localStorage keys.
 * Most-recent-first.
 */
export function useUnseenSpotlights() {
  const queryClient = useQueryClient();
  const instanceId = useId();
  // Read auth from the shared context — captured into the queryFn closure
  // below so the queryFn never has to call supabase.auth.getUser() (which
  // would acquire the auth lock and contend with everyone else).
  const { session } = useUserProfile();
  const userId = session?.user.id ?? null;

  // Live: invalidate the query whenever a spotlight is approved, edited,
  // or deleted so the homepage banner reflects admin changes within
  // seconds instead of waiting on the 2-min stale time.
  useEffect(() => {
    const channel = supabase
      .channel(`unseen-spotlights-live-${instanceId}`)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "market_spotlights" },
        () => {
          queryClient.invalidateQueries({ queryKey: ["unseen-spotlights"] });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [queryClient, instanceId]);

  return useQuery<MarketSpotlight[]>({
    // Re-key on userId so the cache is per-user (otherwise an authenticated
    // and an anonymous user on the same browser would share a stale cache).
    queryKey: ["unseen-spotlights", userId],
    queryFn: async () => {
      // For authenticated users, run both queries in parallel instead of
      // sequentially. Saves ~50-100ms on page load.
      const spotlightsPromise = supabase
        .from("market_spotlights")
        .select("*")
        .eq("status", "approved")
        .order("triggered_at", { ascending: false })
        .limit(20);

      const readsPromise = userId
        ? supabase
            .from("user_spotlight_reads")
            .select("spotlight_id")
            .eq("user_id", userId)
        : null;

      const [spotlightsRes, readsRes] = await Promise.all([
        spotlightsPromise,
        readsPromise,
      ]);

      if (spotlightsRes.error) throw spotlightsRes.error;
      if (!spotlightsRes.data?.length) return [];

      // Filter scheduled / expired client-side (avoids PostgREST ISO-timestamp colon bug)
      const now = new Date();
      const active = (spotlightsRes.data as MarketSpotlight[]).filter((s) => {
        const notYetStarted = s.starts_at && new Date(s.starts_at) > now;
        const expired = s.expires_at && new Date(s.expires_at) <= now;
        return !notYetStarted && !expired;
      });

      if (!userId) {
        return active.filter((s) => !isLocallyDismissed(s.id));
      }

      const seenIds = new Set((readsRes?.data ?? []).map((r: any) => r.spotlight_id));
      return active.filter((s) => !seenIds.has(s.id));
    },
    staleTime: 2 * 60 * 1000,
    refetchIntervalInBackground: false,
  });
}

// ── Mark a spotlight as seen ─────────────────────────────────────────────────

/**
 * Marks a spotlight as seen for the current user.
 * - Always writes to localStorage (instant local dismiss + cross-session fallback).
 * - Writes to DB for authenticated users (cross-device sync).
 */
export function useMarkSpotlightSeen() {
  const qc = useQueryClient();
  // Read auth from context — captured into the mutationFn closure so it
  // never calls supabase.auth.getUser().
  const { session } = useUserProfile();
  const userId = session?.user.id ?? null;

  return useMutation({
    mutationFn: async (spotlightId: string) => {
      localDismiss(spotlightId);
      if (!userId) return;

      await supabase
        .from("user_spotlight_reads")
        .upsert(
          { user_id: userId, spotlight_id: spotlightId },
          { onConflict: "user_id,spotlight_id" }
        );
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["unseen-spotlights"] });
    },
  });
}
